import React, { useEffect, useState } from 'react';

import {
  Box, Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';

import Button from '../../../components/Button';
import DataGrid from '../../../components/DataGrid';
import Modal from '../../../components/Modal';
import {
  useDeleteExamAttemptMutation, useGetExamAttemptsByUserQuery, useLazyGetExamAttemptFileUrlQuery,
} from '../../../services/exams';
import { ExamAttempt } from '../../../services/exams/types';
import { useGetStaffsQuery } from '../../../services/staff';
import { ExamAttemptStatus } from '../../../types/common';
import { SUBJECTS } from '../../../utils/common/constants';
import useDownload from '../../../utils/react/hooks/useDownload';
import useError from '../../../utils/react/hooks/useError';
import { DueDateText } from '../index.styles';

interface AttemptsTabProps {
  marker?: string;
  status: ExamAttemptStatus;
}

const AttemptsTab = ({ marker, status }: AttemptsTabProps) => {
  const [mappedAttempts, setMappedAttempts] = useState<ExamAttempt[] | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<ExamAttempt | undefined>(undefined);

  const {
    data: examAttempts, isLoading: isExamAttemptsLoading,
  } = useGetExamAttemptsByUserQuery({ marker, marked: status === ExamAttemptStatus.Marked });

  const [getDownloadUrl, {
    data: downloadUrlData,
    isError: isDownloadUrlError,
    error: downloadUrlError,
  }] = useLazyGetExamAttemptFileUrlQuery();

  const [deleteExamAttempt, {
    isLoading: isDeleteExamAttemptLoading,
    isSuccess: isDeleteExamAttemptSuccess,
    isError: isDeleteExamAttemptError,
    error: deleteExamAttemptError,
  }] = useDeleteExamAttemptMutation();

  const {
    data: staffs, isLoading: isStaffsLoading,
  } = useGetStaffsQuery({});

  useEffect(() => {
    if (examAttempts?.data) {
      // markedDatetime is undefined until the exam is done marking
      const filtered = examAttempts.data.filter((attempt) =>
        (status === ExamAttemptStatus.Marked ? attempt?.markedDatetime : !attempt?.markedDatetime));

      const mapped = filtered.map((entry) => ({
        ...entry,
        displayedExamLabel: `${SUBJECTS[entry.subject].label} ${entry.examLabel}`,
        displayedStudentName: entry.studentName || '???',
        displayedMarkerName: (staffs?.data.find((x) => x.staffId === entry.markerId)?.name || '???'),
      }));
      setMappedAttempts(mapped);
    }
  }, [examAttempts, status, staffs]);

  useEffect(() => {
    if (isDeleteExamAttemptSuccess) {
      setDeleteTarget(undefined);
    }
  }, [isDeleteExamAttemptSuccess]);

  useDownload(downloadUrlData?.data);

  useError(isDeleteExamAttemptError, deleteExamAttemptError);
  useError(isDownloadUrlError, downloadUrlError);

  return (
    <>
      <Box display="grid" width="100%">
        <DataGrid
          initialState={{
            sorting: {
              sortModel: status.includes(ExamAttemptStatus.Marked) ? [{
                field: 'markedDatetime', sort: 'desc',
              }] : [{
                field: 'dueDatetime', sort: 'asc',
              }],
            },
          }}
          loading={isExamAttemptsLoading || isStaffsLoading || !mappedAttempts}
          getRowHeight={() => 40}
          columns={[
            {
              field: 'creationDatetime',
              headerName: 'Created at',
              width: 200,
              renderCell: ({ row } : any) => (
                dayjs(row.creationDatetime).format('DD MMM YYYY hh:mm a')
              ),
            },
            (status.includes(ExamAttemptStatus.Marked) ? {
              field: 'markedDatetime',
              headerName: 'Marked at',
              width: 200,
              renderCell: ({ row } : any) => (
                dayjs(row.markedDatetime).format('DD MMM YYYY hh:mm a') || '-'
              ),
            } : {
              field: 'dueDatetime',
              headerName: 'Due',
              width: 140,
              renderCell: ({ row } : any) => (
                <DueDateText $overdue={row?.dueDatetime && row.dueDatetime < dayjs().valueOf()}>
                  {row.dueDatetime ? _.upperFirst(dayjs(row.dueDatetime).fromNow()) : '-'}
                </DueDateText>
              ),
              sortComparator: (v1: number, v2: number) => {
                const current = dayjs().valueOf();
                return ((v1 ?? Infinity) - current) - ((v2 ?? Infinity) - current);
              },
            }),
            {
              field: 'displayedExamLabel',
              headerName: 'Exam',
              width: 320,
            },
            {
              field: 'displayedStudentName',
              headerName: 'Student',
              width: 160,
            },
            {
              field: 'displayedMarkerName',
              headerName: 'Marker',
              width: 160,
            },
            {
              field: 'options',
              headerName: 'Options',
              width: 240,
              renderCell: ({ row }: any) => (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="text"
                    href={`/teacher/marking/${row.subject}/${row.examId}/${row.student}/${row.attemptNumber}`}
                  >
                    View
                  </Button>
                  <Button
                    color="info"
                    size="small"
                    variant="text"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      await getDownloadUrl({
                        subject: row.subject,
                        examId: row.examId,
                        studentId: row.student,
                        attemptNum: row.attemptNumber,
                      });
                    }}
                  >
                    Download
                  </Button>
                  <Button
                    color="error"
                    size="small"
                    variant="text"
                    onClick={() => setDeleteTarget(row)}
                  >
                    Delete
                  </Button>
                </Stack>
              ),
            },
          ]}
          rows={mappedAttempts || []}
        />
      </Box>
      <Modal
        open={!!deleteTarget}
        handleClose={() => {
          setDeleteTarget(undefined);
        }}
        title="Are you sure?"
        description="This will permamently delete this exam attempt."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteExamAttempt({
              subject: deleteTarget?.subject || '',
              examId: deleteTarget?.examId || '',
              studentId: deleteTarget?.student || '',
              attemptNum: deleteTarget?.attemptNumber || 0,
            }),
            color: 'error',
            loading: isDeleteExamAttemptLoading,
          },
        ]}
      />
    </>
  );
};

export default AttemptsTab;
